/**
 * Single source of truth for PCBCanvas styling tokens.
 *
 * Lives outside the PCBCanvas component so primitives (PCBFootprint,
 * PCBLayer) can reach for the same colourway without prop-drilling
 * the entire layer-styles map.
 *
 * **Layer model is `LayerKind`-keyed** (mirror of
 * `backend/circuits/ir.py::LayerKind`). Adapters classify raw tokens
 * once at parse time so the renderer + DRC + layer toggles see a
 * closed set instead of free-string KiCad / EasyEDA / Altium names.
 *
 * Inner copper layers get a stepped palette (phosphor green → cyan →
 * amber → red) generated by lerping in HSL through the brand spectrum
 * — engineers reading a 4-, 6-, 8-layer board can visually rank the
 * stackup without reading the layer-control chip.
 *
 * **Aesthetic decisions:**
 *
 *   * **F.Cu = phosphor green, B.Cu = trace amber.** The brand uses
 *     phosphor cyan for "live" and amber for "AI/editorial". B.Cu
 *     lifts the editorial amber so flipped boards still feel on-brand.
 *
 *   * **Footprint outlines colour-key to the layer.** A footprint on
 *     F.Cu renders with phosphor-green outline; flipped to B.Cu it
 *     becomes amber. Engineers learn to read board side at a glance.
 *
 *   * **Inner-copper hue ramp** — phosphor green → cyan → editorial
 *     amber → muted red as we descend the stack. Layer 1 (just below
 *     F.Cu) sits closest to phosphor; layer N (just above B.Cu) sits
 *     closest to amber. Helps disambiguate "which inner is this".
 */
import { alpha } from "@mui/material/styles";

import type { LayerKind, PCBLayer } from "@models/circuit-ir";

export const PCB_LAYER_PALETTE = {
  /** Bright phosphor green — top copper. */
  fCu: "#5be09a",
  /** Trace amber — bottom copper. Pulled from the editorial palette. */
  bCu: "#d4a05c",
  /** Pad/via fills — phosphor cyan, brand accent. */
  pad: "#5be0c8",
  /** Soft amber — board edge cuts. */
  edge: "#f0c794",
  /** Muted ink — silkscreen text/lines. */
  silk: "#e7edf6",
  /** Dim graphite — courtyard / fab outline. Lower-contrast on
   *  purpose; courtyard is reference geometry, not the part. */
  crtyd: "#6b7686",
  /** Slightly cooler graphite — fab outline. */
  fab: "#8c95a4",
  /** Through-hole barrel — paint inside the drill aperture so the
   *  dot grid doesn't bleed through. */
  drill: "#0c1018",
  /** Solder-mask wash — slight teal so it reads as "negative space"
   *  rather than an actual layer. */
  mask: "#1f3640",
  /** Solder-paste tint — warm beige to evoke real paste. */
  paste: "#c9b078",
  /** Last-resort "we don't know what layer this is". Muted slate so
   *  it doesn't compete with the colour-keyed layers. */
  unknown: "#5a6470",
} as const;

/** Hand-picked HSL stops for inner copper layers, walking the brand
 *  spectrum top → bottom: phosphor green (top adjacent) → phosphor
 *  cyan → editorial blue → amber → muted red (bottom adjacent). The
 *  index→colour mapping clamps to this list when the stackup is
 *  shallower than the palette and interpolates when deeper. */
const INNER_COPPER_RAMP = [
  "#7ce3a0", // 1 — slightly bluer than F.Cu
  "#5fd7c2", // 2 — phosphor cyan
  "#5cc7d4", // 3 — sky cyan
  "#5fa4d2", // 4 — sky blue
  "#7c8fcf", // 5 — periwinkle
  "#a583c8", // 6 — soft violet
  "#c87aaf", // 7 — magenta-rose
  "#dc8b85", // 8 — rose
  "#e3a06c", // 9 — apricot (reads close to bCu so 9+ start blending)
  "#d6b85a", // 10 — gold
] as const;

/** Resolve an inner-copper layer's stroke colour from its 1-based
 *  index. Saturates at the end of `INNER_COPPER_RAMP` for boards
 *  with more inner layers than ramp stops — KiCad's max is 30 inner
 *  copper layers, but past 10 the ramp distinctions get visually
 *  hard anyway. */
export function innerCopperStroke(index: number): string {
  if (index < 1) return INNER_COPPER_RAMP[0];
  const i = Math.min(index, INNER_COPPER_RAMP.length) - 1;
  return INNER_COPPER_RAMP[i];
}

/** Custom-layer colour ramp — used for `LayerKind.USER` (the bucket
 *  we drop CAD-specific custom / mechanical / documentation layers
 *  into). KiCad alone exposes User.1..User.9, Eco1.User, Eco2.User,
 *  Cmts.User, Dwgs.User; Altium has Mechanical 1..16; EasyEDA has
 *  Document. Without per-layer colouring all ten collapse into one
 *  grey, defeating the purpose of having user layers in the first
 *  place. The hash-derived colour-from-name ensures the same custom
 *  layer always paints the same way across saves / reloads. */
const USER_LAYER_RAMP = [
  "#c79b7e", // muted terracotta
  "#a89060", // ochre
  "#8c9b7e", // sage
  "#7e9bb0", // dust blue
  "#9b7eb0", // muted plum
  "#b07e9b", // mauve
  "#7eb09b", // teal-green
  "#b09b7e", // pale gold
  "#7eaeb0", // muted teal
  "#a07eb0", // soft purple
] as const;

/** Stable index from a string — DJB2 hash modded into the user ramp.
 *  Same input always yields the same colour. Fine for the visual
 *  distinction we want; this isn't a security boundary. */
function hashLayerName(name: string): number {
  let h = 5381;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) + h + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % USER_LAYER_RAMP.length;
}

/** Resolve a user-layer stroke colour from its raw name. Each
 *  distinct name gets its own colour from the ramp; reuse of the
 *  same name across saves keeps its colour stable. */
export function userLayerStroke(name: string): string {
  return USER_LAYER_RAMP[hashLayerName(name)];
}

/** Convention-based colours for the netclass names engineers
 *  recognise on sight. Hit-rate matters more than coverage — these
 *  are the names the user expects to see in colour without any
 *  preference setting. Anything not in this map falls through to
 *  the hash-derived ramp, which is stable but arbitrary.
 *
 *  Lookup is case-insensitive + ignores trailing digits ("Power_3V3"
 *  → "power", "DiffPair_USB" → "diff" via the prefix match below).
 *  KiCad / EasyEDA / Altium projects all converge on this naming
 *  vocabulary; treating it as a soft enum lets the viewer feel
 *  "smart" without anyone configuring anything. */
const NETCLASS_CONVENTION_COLOURS: Record<string, string> = {
  // Power rails — strong amber so they read "this is the bus".
  power: "#e0a050",
  vcc: "#e0a050",
  vdd: "#e0a050",
  // Ground / return — muted graphite so it recedes (engineers want
  // signal nets to stand out, not GND).
  ground: "#7a8896",
  gnd: "#7a8896",
  // High-speed digital — bright phosphor cyan so it pops on busy
  // boards. The "look at me" net class.
  highspeed: "#5be0d4",
  highspeeddiff: "#5be0d4",
  // Differential pairs — magenta-rose so a pair reads as one
  // visually-coupled signal.
  diff: "#d678a8",
  diffpair: "#d678a8",
  // Clock / strobe — orchid so clocks read distinct from data.
  clock: "#a878d6",
  clk: "#a878d6",
  // Analog — sage green so it doesn't compete with the digital
  // colours. Mirrors the convention many CAD users learn from
  // KiCad's stock palette.
  analog: "#9bb89b",
  // Default class — fall through to layer colour rather than
  // colouring everything the same.
  default: "",
};

/** Hash-derived netclass ramp — used for class names not in the
 *  convention map. Distinct from the user-layer ramp so the eye
 *  doesn't conflate "this is on layer X" with "this is class Y". */
const NETCLASS_HASH_RAMP = [
  "#e6a48e", // peach
  "#bfd07a", // lime
  "#86cdb1", // mint
  "#8cb6e6", // sky
  "#b08cd0", // lilac
  "#e09a9a", // rose
  "#d6c578", // straw
  "#a0c7c2", // pewter
  "#c1a0d6", // wisteria
  "#a8d6a0", // pistachio
] as const;

function _classNameKey(name: string): string {
  // Strip leading non-letters, lowercase, trim trailing digits / _ /
  // dash / spaces. "Power_3V3" → "power", "DiffPair_USB_DP" → "diff"
  // via the prefix matches in the convention table.
  return name.toLowerCase().replace(/^[^a-z]+/, "").replace(/[_\-0-9 ]+.*$/, "");
}

/** Resolve a netclass stroke colour. Returns `null` when the
 *  caller should fall through to the host layer's stroke (the
 *  "Default" class behaviour) — that's not a failure, it's the
 *  intentional "this net has no special class, paint with its
 *  layer" path. */
export function netClassColour(name: string): string | null {
  if (!name) return null;
  // Direct match first — "USB", "DDR4", etc. that the user might
  // have set as their netclass.
  const lower = name.toLowerCase();
  if (lower in NETCLASS_CONVENTION_COLOURS) {
    return NETCLASS_CONVENTION_COLOURS[lower] || null;
  }
  // Then the stripped-prefix form for "Power_3V3" / "DiffPair_USB".
  const key = _classNameKey(name);
  if (key in NETCLASS_CONVENTION_COLOURS) {
    return NETCLASS_CONVENTION_COLOURS[key] || null;
  }
  // Fall through to the hash ramp — distinct, stable, but the
  // viewer's "we don't recognise this name" tier.
  let h = 5381;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) + h + name.charCodeAt(i)) | 0;
  }
  return NETCLASS_HASH_RAMP[Math.abs(h) % NETCLASS_HASH_RAMP.length];
}

export interface PcbLayerStyle {
  /** Stroke colour for tracks. */
  stroke: string;
  /** Glow colour for hover / net-cascade highlight. */
  glow: string;
  /** Display label shown in the LayerControl chip. */
  label: string;
  /** Z-order — lower numbers paint first (under). */
  z: number;
}

/** Resolve the layer style for a given LayerKind + optional
 *  inner-copper index + optional layer name (for USER / UNKNOWN
 *  kinds where the name disambiguates between e.g. User.1 and
 *  Mechanical 5). The single function the renderer calls — keeps
 *  the kind dispatch in one place and prevents drift across the
 *  primitives. */
export function styleForKind(
  kind: LayerKind | undefined,
  innerIndex?: number | null,
  amber: string = PCB_LAYER_PALETTE.bCu,
  rawName?: string,
): PcbLayerStyle {
  switch (kind) {
    case "copper_top":
      return {
        stroke: PCB_LAYER_PALETTE.fCu,
        glow: alpha(PCB_LAYER_PALETTE.fCu, 0.45),
        label: "Top Cu",
        z: 30,
      };
    case "copper_bottom":
      return {
        stroke: amber,
        glow: alpha(amber, 0.45),
        label: "Bot Cu",
        z: 10,
      };
    case "copper_inner": {
      const colour = innerCopperStroke(innerIndex ?? 1);
      return {
        stroke: colour,
        glow: alpha(colour, 0.4),
        label: `In${innerIndex ?? "?"}`,
        // Inner copper sits between top (z=30) and bottom (z=10).
        // Distribute by index so deeper layers paint under shallower.
        z: 30 - Math.min(innerIndex ?? 1, 19),
      };
    }
    case "silk_top":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.silk, 0.55),
        glow: alpha(PCB_LAYER_PALETTE.silk, 0.25),
        label: "Top Silk",
        z: 50,
      };
    case "silk_bottom":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.silk, 0.35),
        glow: alpha(PCB_LAYER_PALETTE.silk, 0.18),
        label: "Bot Silk",
        z: 51,
      };
    case "mask_top":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.mask, 0.6),
        glow: alpha(PCB_LAYER_PALETTE.mask, 0.3),
        label: "Top Mask",
        z: 25,
      };
    case "mask_bottom":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.mask, 0.45),
        glow: alpha(PCB_LAYER_PALETTE.mask, 0.22),
        label: "Bot Mask",
        z: 11,
      };
    case "paste_top":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.paste, 0.6),
        glow: alpha(PCB_LAYER_PALETTE.paste, 0.3),
        label: "Top Paste",
        z: 31,
      };
    case "paste_bottom":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.paste, 0.45),
        glow: alpha(PCB_LAYER_PALETTE.paste, 0.22),
        label: "Bot Paste",
        z: 9,
      };
    case "fab_top":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.fab, 0.55),
        glow: alpha(PCB_LAYER_PALETTE.fab, 0.25),
        label: "Top Fab",
        z: 60,
      };
    case "fab_bottom":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.fab, 0.35),
        glow: alpha(PCB_LAYER_PALETTE.fab, 0.18),
        label: "Bot Fab",
        z: 61,
      };
    case "courtyard_top":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.crtyd, 0.5),
        glow: alpha(PCB_LAYER_PALETTE.crtyd, 0.22),
        label: "Top CrtYd",
        z: 70,
      };
    case "courtyard_bottom":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.crtyd, 0.32),
        glow: alpha(PCB_LAYER_PALETTE.crtyd, 0.16),
        label: "Bot CrtYd",
        z: 71,
      };
    case "edge_cuts":
      return {
        stroke: alpha(PCB_LAYER_PALETTE.edge, 0.9),
        glow: alpha(PCB_LAYER_PALETTE.edge, 0.4),
        label: "Edge",
        z: 0,
      };
    case "drill":
      return {
        stroke: PCB_LAYER_PALETTE.drill,
        glow: alpha(PCB_LAYER_PALETTE.drill, 0.6),
        label: "Drill",
        z: 80,
      };
    case "user": {
      // Custom layers (KiCad User.1..9 / Eco.* / Cmts / Dwgs,
      // Altium Mechanical 1..16, EasyEDA Document) each get their
      // own stable hash-derived colour off USER_LAYER_RAMP. Falling
      // back to the muted-grey path when no name was supplied (legacy
      // overlays) so we never crash, just lose the per-layer hue.
      const colour = rawName
        ? userLayerStroke(rawName)
        : alpha(PCB_LAYER_PALETTE.unknown, 0.7);
      return {
        stroke: colour,
        glow: alpha(colour, 0.3),
        label: rawName ?? "User",
        z: 90,
      };
    }
    case "unknown":
    default: {
      // Truly unrecognised tokens — render in muted grey so the
      // user can see "something is here, but we don't know what" and
      // surface a hue derived from the raw name (when available) so
      // multiple unknown layers don't collapse into one mass.
      const colour = rawName
        ? userLayerStroke(rawName)
        : alpha(PCB_LAYER_PALETTE.unknown, 0.5);
      return {
        stroke: alpha(colour, 0.55),
        glow: alpha(colour, 0.22),
        label: rawName ?? "Other",
        z: 100,
      };
    }
  }
}

/** Best-effort fallback: derive a LayerKind from a raw layer-name
 *  string, for legacy overlays saved before the backend started
 *  populating `Layer.kind`. The full normaliser lives backend-side;
 *  this is a smaller, frontend-only mirror covering the most common
 *  KiCad / EasyEDA / Altium tokens — enough to not lose colour fidelity
 *  on cached overlays. New overlays come down with `kind` already
 *  populated, so this code path is a one-way compat ramp. */
export function layerKindFromName(raw: string): {
  kind: LayerKind;
  innerIndex: number | null;
} {
  if (raw.startsWith("In") && raw.endsWith(".Cu")) {
    const idx = Number.parseInt(raw.slice(2, -3), 10);
    if (!Number.isNaN(idx) && idx >= 1 && idx <= 30) {
      return { kind: "copper_inner", innerIndex: idx };
    }
  }
  const direct: Record<string, LayerKind> = {
    "F.Cu": "copper_top",
    "B.Cu": "copper_bottom",
    "F.SilkS": "silk_top",
    "B.SilkS": "silk_bottom",
    "F.Mask": "mask_top",
    "B.Mask": "mask_bottom",
    "F.Paste": "paste_top",
    "B.Paste": "paste_bottom",
    "F.Fab": "fab_top",
    "B.Fab": "fab_bottom",
    "F.CrtYd": "courtyard_top",
    "B.CrtYd": "courtyard_bottom",
    "Edge.Cuts": "edge_cuts",
    "*.Cu": "copper_top",
  };
  if (raw in direct) {
    return { kind: direct[raw], innerIndex: null };
  }
  const folded = raw.replace(/[\s_-]/g, "").toLowerCase();
  if (folded === "toplayer") return { kind: "copper_top", innerIndex: null };
  if (folded === "bottomlayer") {
    return { kind: "copper_bottom", innerIndex: null };
  }
  if (folded.startsWith("inner")) {
    const idx = Number.parseInt(folded.slice(5), 10);
    if (!Number.isNaN(idx) && idx >= 1 && idx <= 30) {
      return { kind: "copper_inner", innerIndex: idx };
    }
  }
  return { kind: "unknown", innerIndex: null };
}

/** Lift `kind` + `inner_index` off a PCBLayer model, falling back to
 *  the name-string mirror when the backend hasn't populated kind yet
 *  (legacy overlays, or test fixtures). Single helper the renderer
 *  uses everywhere it needs the kind for a layer. */
export function resolveLayerKind(layer: PCBLayer): {
  kind: LayerKind;
  innerIndex: number | null;
} {
  if (layer.kind && layer.kind !== "unknown") {
    return {
      kind: layer.kind,
      innerIndex: layer.inner_index ?? null,
    };
  }
  return layerKindFromName(layer.name);
}

/** Build the layer-styles map keyed by raw layer name — kept for
 *  backwards-compatibility with primitives that still take a raw
 *  layer-name `Record<string, PcbLayerStyle>`. New code should call
 *  `styleForKind` directly. */
export function buildPcbLayerStyles(
  layers: PCBLayer[],
  amber: string = PCB_LAYER_PALETTE.bCu,
): Record<string, PcbLayerStyle> {
  const out: Record<string, PcbLayerStyle> = {};
  for (const layer of layers) {
    const { kind, innerIndex } = resolveLayerKind(layer);
    out[layer.name] = styleForKind(kind, innerIndex, amber, layer.name);
  }
  return out;
}

/** Style for a raw layer-name string the renderer encounters in
 *  data that didn't ship through a Layer record (e.g. a track that
 *  cites a layer name not present in `overlay.layers`). Falls back
 *  through `layerKindFromName` so the colour is consistent with
 *  whatever the same name would have been styled to via a Layer. */
export function styleForLayerName(
  rawName: string,
  amber: string = PCB_LAYER_PALETTE.bCu,
): PcbLayerStyle {
  const { kind, innerIndex } = layerKindFromName(rawName);
  return styleForKind(kind, innerIndex, amber, rawName);
}

/** Footprint outline dimensions per component kind, in mm. The PCB IR
 *  doesn't carry footprint geometry yet (V2), so we render a kind-keyed
 *  bounding box that loosely matches the typical SMT package size for
 *  that kind. The shapes are intentionally schematic-flat — engineers
 *  reading the board don't expect courtyard-perfect outlines from a
 *  preview tool. */
export const FOOTPRINT_SIZE: Record<string, { w: number; h: number }> = {
  resistor: { w: 3.2, h: 1.6 },
  capacitor: { w: 3.2, h: 1.6 },
  inductor: { w: 4.0, h: 2.5 },
  diode: { w: 2.6, h: 1.4 },
  mosfet: { w: 4.0, h: 4.0 },
  bjt: { w: 2.8, h: 2.8 },
  opamp: { w: 5.0, h: 4.0 },
  ic: { w: 6.0, h: 6.0 },
  header: { w: 5.0, h: 2.5 },
  source: { w: 6.0, h: 4.0 },
  rail: { w: 2.0, h: 2.0 },
  other: { w: 3.0, h: 2.0 },
};

export function footprintSize(kind: string): { w: number; h: number } {
  return FOOTPRINT_SIZE[kind] ?? FOOTPRINT_SIZE.other;
}

/** Convert a KiCad 3-point arc (start, mid, end) to an SVG path `d`
 *  attribute. Computes the circumcircle's centre + radius via
 *  perpendicular bisectors, then derives the SVG large-arc / sweep
 *  flags from which side of the start→end vector the midpoint sits.
 *  Falls back to a straight line when the three points are colinear. */
export function arcPathD(
  start: [number, number],
  mid: [number, number],
  end: [number, number],
): string {
  const [ax, ay] = start;
  const [bx, by] = mid;
  const [cx, cy] = end;
  const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
  if (Math.abs(d) < 1e-9) {
    return `M ${ax} ${ay} L ${cx} ${cy}`;
  }
  const ux =
    ((ax * ax + ay * ay) * (by - cy) +
      (bx * bx + by * by) * (cy - ay) +
      (cx * cx + cy * cy) * (ay - by)) /
    d;
  const uy =
    ((ax * ax + ay * ay) * (cx - bx) +
      (bx * bx + by * by) * (ax - cx) +
      (cx * cx + cy * cy) * (bx - ax)) /
    d;
  const r = Math.hypot(ax - ux, ay - uy);
  const cross = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  const sweep = cross > 0 ? 1 : 0;
  const a0 = Math.atan2(ay - uy, ax - ux);
  const a2 = Math.atan2(cy - uy, cx - ux);
  const am = Math.atan2(by - uy, bx - ux);
  const norm = (a: number) => (a - a0 + Math.PI * 2) % (Math.PI * 2);
  const dEnd = norm(a2);
  const dMid = norm(am);
  const onShort = sweep === 1 ? dMid < dEnd : dMid > dEnd;
  const large = onShort ? 0 : 1;
  return `M ${ax} ${ay} A ${r} ${r} 0 ${large} ${sweep} ${cx} ${cy}`;
}
